import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";

import Header from "../../components/Header";
import { useEffect, useMemo, useState } from "react";
import API from "../../api";
import { palette } from "../../theme";
import ImageTemplates from "../../components/ImageTemplates";
import { Panel, MUTED, BORDER } from "../dashboard/dashboard-components/resellerUi";

import {
  startToast,
  updateToastSuccess,
  updateToastError,
} from "../../utils/notification-loading";

const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const outlineBtnSx = {
  color: "#eaeaea",
  borderColor: "rgba(255,255,255,0.14)",
  "&:hover": { borderColor: palette.primary.light, backgroundColor: "rgba(69,144,149,0.12)" },
};

// Riga di opzione con switch e descrizione.
const OptionRow = ({ label, description, checked, onChange }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
    <Box>
      <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, color: MUTED }}>{description}</Typography>
    </Box>
    <Switch checked={checked} onChange={onChange} />
  </Box>
);

const Report = () => {
  // [{ name, email }] ordinati per nome; la selezione è per email (unica).
  const [clientsData, setClientsData] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);

  const [selectedWeekday, setSelectedWeekday] = useState("");

  const [checkTiresia, setCheckTiresia] = useState(false);
  const [checkAssets, setCheckAssets] = useState(false);
  const [openAssetsInfoDialog, setOpenAssetsInfoDialog] = useState(false);

  const [saving, setSaving] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(true);

  /**
   * Questo flag controlla l'attivazione del pulsante SAVE.
   * Diventa true quando l'utente modifica clienti, giorno, Tiresia o Assets.
   */
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const customersResponse = await API.getCustomers();

        const clients = customersResponse.data
          .filter((item) => item.email)
          .map((item) => ({ name: item.ragioneSociale || item.email, email: item.email }))
          .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true })
          );

        setClientsData(clients);

        const reportResponse = await API.checkInfoReport();

        if (reportResponse.data.exists) {
          const known = new Set(clients.map((c) => c.email));
          const emails = (reportResponse.data.info.emails || []).filter((e) => known.has(e));

          setSelectedEmails(new Set(emails));
          setSelectedWeekday(reportResponse.data.info.day || "");
          setCheckTiresia(!!reportResponse.data.info.tiresia);
          setCheckAssets(!!reportResponse.data.info.assets);
        } else {
          setSelectedEmails(new Set());
          setSelectedWeekday("");
          setCheckTiresia(false);
          setCheckAssets(false);
        }

        /**
         * Importante:
         * il caricamento iniziale NON deve attivare il pulsante SAVE.
         */
        setHasChanges(false);
      } catch (err) {
        console.error('[loadData] Error while loading preferences:', err);
        startToast();
        updateToastError("Error while loading preferences");
      } finally {
        setLoadingPreferences(false);
      }
    };

    loadData();
  }, []);

  const visibleClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clientsData.filter(
      (c) =>
        (!onlySelected || selectedEmails.has(c.email)) &&
        (!q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))
    );
  }, [clientsData, search, onlySelected, selectedEmails]);

  const allVisibleSelected =
    visibleClients.length > 0 && visibleClients.every((c) => selectedEmails.has(c.email));

  const toggleClient = (email) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
    setHasChanges(true);
  };

  // Seleziona/deseleziona tutti i clienti attualmente visibili (rispetta la ricerca).
  const toggleAllVisible = () => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      visibleClients.forEach((c) => (allVisibleSelected ? next.delete(c.email) : next.add(c.email)));
      return next;
    });
    setHasChanges(true);
  };

  const handleWeekdayChange = (_, value) => {
    setSelectedWeekday(value ?? "");
    setHasChanges(true);
  };

  const handleTiresiaChange = (e) => {
    setCheckTiresia(e.target.checked);
    setHasChanges(true);
  };

  const handleAssetsChange = (e) => {
    const checked = e.target.checked;
    setCheckAssets(checked);
    setHasChanges(true);

    // Questo report copre più clienti insieme: a differenza del report
    // singolo, qui non blocchiamo l'attivazione se un cliente non ha Orion —
    // avvisiamo solo che quei clienti non avranno la sezione Assets.
    if (checked) {
      setOpenAssetsInfoDialog(true);
    }
  };

  const handleRemoveAll = () => {
    setSelectedEmails(new Set());
    setSelectedWeekday("");
    setCheckTiresia(false);
    setCheckAssets(false);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (saving) return;

    setSaving(true);

    try {
      const isEmptyConfiguration =
        selectedEmails.size === 0 &&
        !selectedWeekday &&
        !checkTiresia &&
        !checkAssets;

      if (isEmptyConfiguration) {
        await API.deleteInfoReport();

        startToast();
        updateToastSuccess("Preferences deleted");

        setHasChanges(false);
        return;
      }

      if (selectedEmails.size === 0 || !selectedWeekday) {
        startToast();
        updateToastError("Select at least one customer and one day");
        return;
      }

      const payload2 = {
        emails: clientsData.filter((c) => selectedEmails.has(c.email)).map((c) => c.email),
        day: selectedWeekday,
        tiresia: checkTiresia,
        assets: checkAssets,
      };

      const check = await API.checkInfoReport();
      const exists = !!check?.data?.exists;

      if (!exists) {
        try {
          await API.addInfoReport(payload2);

          startToast();
          updateToastSuccess("Saved preferences");
        } catch (errAdd) {
          if (errAdd?.response?.status === 409) {
            await API.updateInfoReport(payload2);

            startToast();
            updateToastSuccess("Preferences updated");
          } else {
            throw errAdd;
          }
        }
      } else {
        await API.updateInfoReport(payload2);

        startToast();
        updateToastSuccess("Preferences updated");
      }

      /**
       * Dopo il salvataggio riuscito,
       * il pulsante SAVE torna disabilitato finché non modifichi di nuovo qualcosa.
       */
      setHasChanges(false);
    } catch (err) {
      console.error('[handleSave] Error while saving:', err);
      startToast();
      updateToastError("Error while saving");
    } finally {
      setSaving(false);
    }
  };

  const customersToolbar = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
      <TextField
        size="small"
        placeholder="Search customer by name or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ minWidth: 240, flex: 1 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: MUTED }} />
              </InputAdornment>
            ),
          },
        }}
      />
      <Chip
        size="small"
        clickable
        onClick={() => setOnlySelected((v) => !v)}
        label={`Selected · ${selectedEmails.size}`}
        sx={{
          color: onlySelected ? "#eaeaea" : MUTED,
          backgroundColor: onlySelected ? "rgba(69,144,149,0.18)" : "transparent",
          border: "1px solid",
          borderColor: onlySelected ? palette.primary.main : "rgba(255,255,255,0.14)",
        }}
      />
      <Button size="small" variant="outlined" onClick={toggleAllVisible} disabled={visibleClients.length === 0} sx={outlineBtnSx}>
        {allVisibleSelected ? "Deselect" : "Select"} {search || onlySelected ? "shown" : "all"}
      </Button>
    </Box>
  );

  return (
    <Box m="20px">
      <Header title="Generate weekly report" />

      <Alert severity="info" variant="outlined" sx={{ mt: 2, mb: 2, borderColor: BORDER, color: MUTED }}>
        This page is being released in beta for a limited time and is intended for trial use only.
      </Alert>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <Panel
            icon={GroupsOutlinedIcon}
            title="Customers"
            toolbar={customersToolbar}
            sx={{ height: "calc(100vh - 290px)", minHeight: 420 }}
          >
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              {loadingPreferences ? (
                <Typography sx={{ p: 2, color: MUTED }}>Loading…</Typography>
              ) : visibleClients.length === 0 ? (
                <Typography sx={{ p: 2, color: MUTED }}>No customers found.</Typography>
              ) : (
                visibleClients.map((c) => {
                  const selected = selectedEmails.has(c.email);
                  return (
                    <Box
                      key={c.email}
                      role="checkbox"
                      aria-checked={selected}
                      tabIndex={0}
                      onClick={() => toggleClient(c.email)}
                      onKeyDown={(e) => {
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          toggleClient(c.email);
                        }
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        cursor: "pointer",
                        borderBottom: `1px solid ${BORDER}`,
                        borderLeft: "3px solid",
                        borderLeftColor: selected ? palette.primary.main : "transparent",
                        backgroundColor: selected ? "rgba(69,144,149,0.14)" : "transparent",
                        "&:hover": { backgroundColor: selected ? "rgba(69,144,149,0.2)" : "rgba(255,255,255,0.05)" },
                        "&:focus-visible": { outline: `1px solid ${palette.primary.light}`, outlineOffset: -1 },
                      }}
                    >
                      <Checkbox checked={selected} tabIndex={-1} disableRipple size="small" sx={{ p: 0.5, pointerEvents: "none" }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: selected ? 700 : 500 }} noWrap>{c.name}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: MUTED }} noWrap>{c.email}</Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Panel>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Panel
            icon={EventRepeatOutlinedIcon}
            title="Schedule"
            sx={{ height: "auto", minHeight: 0 }}
          >
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 3 }}>
              <Box>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Send every</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={selectedWeekday || null}
                  onChange={handleWeekdayChange}
                  sx={{ flexWrap: "wrap", gap: 0.75, "& .MuiToggleButtonGroup-grouped": { border: "1px solid rgba(255,255,255,0.14) !important", borderRadius: "8px !important", px: 1.5 } }}
                >
                  {WEEKDAYS.map((day) => (
                    <ToggleButton
                      key={day}
                      value={day}
                      sx={{
                        color: MUTED,
                        textTransform: "none",
                        "&.Mui-selected": { color: "#eaeaea", backgroundColor: "rgba(69,144,149,0.22)", borderColor: `${palette.primary.main} !important` },
                        "&.Mui-selected:hover": { backgroundColor: "rgba(69,144,149,0.3)" },
                      }}
                    >
                      {day.slice(0, 3)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.75 }}>
                  {selectedWeekday ? `Every ${selectedWeekday}` : "No day selected"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>Sections</Typography>
                <OptionRow
                  label="Tiresia"
                  description="Include the Tires-IA behavioral analysis."
                  checked={checkTiresia}
                  onChange={handleTiresiaChange}
                />
                <OptionRow
                  label="Orion"
                  description="Include the Orion asset analysis. Only for customers with the ORION module."
                  checked={checkAssets}
                  onChange={handleAssetsChange}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, pt: 1, borderTop: `1px solid ${BORDER}` }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || loadingPreferences || !hasChanges}
                  sx={{ minWidth: 120 }}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button variant="outlined" onClick={handleRemoveAll} sx={outlineBtnSx}>
                  Remove all
                </Button>
              </Box>
            </Box>
          </Panel>

          <ImageTemplates />
        </Grid>
      </Grid>

      <Dialog
        open={openAssetsInfoDialog}
        onClose={() => setOpenAssetsInfoDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Orion enabled</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Enabling Orion will generate the corresponding sections only for customers that have the ORION module active.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenAssetsInfoDialog(false)} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Report;
